import { useState, useEffect, useCallback } from "react";
import { useResize } from "@churchofjesuschrist/eden-resize-observer";
import PropTypes from "prop-types";

const throttle = function (fn) {
  let time = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 50;
  let timer = null;

  function throttledFn() {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    if (!timer) {
      timer = setTimeout(() => {
        fn(...args);
        timer = null;
      }, time);
    }
  }

  throttledFn.cancel = () => {
    clearTimeout(timer);
    timer = null;
  };

  return throttledFn;
};

const HasOverflow = _ref => {
  let {
    children,
    startX = false,
    startY = false,
    start = false,
    end = false,
    left = false,
    right = false,
    top = false,
    bottom = false,
    fromStart = 0,
    fromEnd = 0,
    fromTop = 0,
    fromBottom = 0
  } = _ref;
  const [overflowRef, overflowValues] = useHasOverflow(false, {
    startX: startX,
    startY: startY,
    start: start,
    end: end,
    left: left,
    right: right,
    top: top,
    bottom: bottom,
    fromStart: fromStart,
    fromEnd: fromEnd,
    fromTop: fromTop,
    fromBottom: fromBottom
  });
  return children({
    refHandler: overflowRef,
    ...overflowValues
  });
};

HasOverflow.propTypes = {
  /** Render prop which is is called with wrapping element overflow data */
  children: PropTypes.func.isRequired,

  /** Whether there is X overflow by default */
  startX: PropTypes.bool,

  /** Whether there is Y overflow by default */
  startY: PropTypes.bool,

  /** Whether there is Start overflow by default */
  start: PropTypes.bool,

  /** Whether there is End overflow by default */
  end: PropTypes.bool,

  /** Whether there is Left overflow by default */
  left: PropTypes.bool,

  /** Whether there is Right overflow by default */
  right: PropTypes.bool,

  /** Whether there is Top overflow by default */
  top: PropTypes.bool,

  /** Whether there is Bottom overflow by default */
  bottom: PropTypes.bool,

  /** Space from start content to container */
  fromStart: PropTypes.number,

  /** Space from end content to container */
  fromEnd: PropTypes.number,

  /** Space from top content to container */
  fromTop: PropTypes.number,

  /** Space from bottom content to container */
  fromBottom: PropTypes.number
};
export default HasOverflow;
export const useHasOverflow = function (update) {
  let defaultValues = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
    startX: false,
    startY: false,
    start: false,
    end: false,
    left: false,
    right: false,
    top: false,
    bottom: false,
    fromStart: 0,
    fromEnd: 0,
    fromTop: 0,
    fromBottom: 0
  };
  const [overflowValues, setOverflowValues] = useState({
    hasX: defaultValues.startX,
    hasY: defaultValues.startY,
    hasStart: defaultValues.start,
    hasEnd: defaultValues.end,
    hasTop: defaultValues.top,
    hasLeft: defaultValues.left,
    hasRight: defaultValues.right,
    hasBottom: defaultValues.bottom,
    fromStart: defaultValues.fromStart,
    fromEnd: defaultValues.fromEnd,
    fromTop: defaultValues.fromTop,
    fromBottom: defaultValues.fromBottom
  });
  const [direction, setDirection] = useState("");
  const [scrollType, setScrollType] = useState("indeterminate");
  const [dimensions, setDimensions] = useState(0);
  const onResize = useCallback(resizeEntry => {
    setDimensions(Math.round(resizeEntry.contentRect.width + resizeEntry.contentRect.height));
  }, []);
  const [resizeRef] = useResize(onResize);
  useEffect(() => {
    // Based on https://stackoverflow.com/a/24394376
    const getScrollLeft = () => {
      const {
        clientWidth,
        scrollWidth,
        scrollLeft
      } = resizeRef.current || {}; // Perform the calculations only when direction is rtl to avoid messing up the ltr behavior

      if (direction !== "rtl") {
        return scrollLeft;
      }

      if (scrollType === "indeterminate") {
        return Number.NaN;
      }

      switch (scrollType) {
        case "negative":
          return scrollWidth - clientWidth + scrollLeft;

        case "reverse":
          return scrollWidth - clientWidth - scrollLeft;
      }

      return scrollLeft;
    };

    const checkOverflow = throttle(() => {
      if (resizeRef.current) {
        const {
          clientHeight,
          scrollHeight,
          clientWidth,
          scrollWidth,
          scrollTop
        } = resizeRef.current;
        const scrollLeft = getScrollLeft();
        const left = scrollLeft > 1;
        const right = scrollWidth - clientWidth - scrollLeft > 1;
        setOverflowValues({
          hasX: clientWidth < scrollWidth,
          hasY: clientHeight < scrollHeight,
          hasStart: direction === "ltr" ? left : scrollLeft < scrollWidth - clientWidth,
          hasEnd: direction === "ltr" ? right : scrollLeft > 0,
          hasTop: scrollTop > 1,
          hasLeft: left,
          hasRight: right,
          hasBottom: scrollHeight - clientHeight - scrollTop > 1,
          fromStart: scrollLeft,
          fromEnd: scrollWidth - clientWidth - scrollLeft,
          fromTop: scrollTop,
          fromBottom: scrollHeight - clientHeight - scrollTop,
          scrollType: scrollType
        });
      }
    }, 125); // From https://github.com/alitaheri/normalize-scroll-left

    const detectScrollType = () => {
      let tempScrollType = scrollType;

      if (tempScrollType !== "indeterminate") {
        return;
      }

      const dummy = window.document.createElement("div");
      dummy.appendChild(document.createTextNode("ABCD"));
      dummy.dir = "rtl";
      dummy.style.width = "4px";
      dummy.style.height = "1px";
      dummy.style.position = "absolute";
      dummy.style.top = "-1000px";
      dummy.style.overflow = "scroll";
      document.body.appendChild(dummy);
      tempScrollType = "reverse";

      if (dummy.scrollLeft > 0) {
        tempScrollType = "default";
      } else {
        dummy.scrollLeft = 1;

        if (dummy.scrollLeft === 0) {
          tempScrollType = "negative";
        }
      }

      document.body.removeChild(dummy);
      setScrollType(tempScrollType);
    };

    detectScrollType();
    checkOverflow();

    if (resizeRef.current) {
      setDirection(resizeRef.current.closest("[dir='rtl']") ? "rtl" : "ltr");
      resizeRef.current.addEventListener("scroll", checkOverflow);
    }

    const currentFirstChild = resizeRef.current;
    return () => {
      checkOverflow.cancel();

      if (currentFirstChild) {
        currentFirstChild.removeEventListener("scroll", checkOverflow);
      }
    };
  }, [direction, scrollType, update, resizeRef, dimensions]);
  return [resizeRef, { ...overflowValues,
    direction
  }];
};